import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT_CS } from "@constants";

type DatePickerFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues, Record<string, unknown>, unknown>;
  label?: string;
  name: Path<TFieldValues>;
  disabled?: boolean;
};

const DatePickerField = <TFieldValues extends FieldValues>({
  control,
  label,
  name,
  disabled,
}: DatePickerFieldProps<TFieldValues>): JSX.Element => {
  const [open, setOpen] = useState(false);

  const { i18n } = useTranslation();
  const format =
    i18n.resolvedLanguage === "cs"
      ? DEFAULT_DATE_FORMAT_CS
      : DEFAULT_DATE_FORMAT;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Controller
        name={name}
        control={control}
        disabled={disabled}
        render={({ field: { onChange, value, ref } }) => (
          <MuiDatePicker
            label={label}
            onChange={(date) => onChange(date?.toDate() ? date.toISOString() : null)}
            onClose={() => setOpen(false)}
            value={dayjs(value).isValid() ? dayjs(value) : null}
            inputRef={ref}
            format={format}
            slotProps={{
              textField: {
                fullWidth: true,
                onClick: () => setOpen(true),
                sx: {
                  "& .MuiInputBase-root": {
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    padding: "14px",
                  },
                },
              },
              inputAdornment: {
                onClick: () => setOpen(true),
              },
              actionBar: {
                actions: ["clear"],
              },
              popper: {
                placement: "bottom-end",
              },
            }}
            open={open}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default DatePickerField;
